import { BellIcon, CounterClockwiseClockIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IAdminAlert } from "../../../../@types/admin/AdminAlert";
import { TableSorted } from "../../../../components/TableSorted";
import { useAdminAlerts } from "../../../../hooks/queries/admin/alerts";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  RowActionGroup,
  SearchInput,
  StatusBadge,
} from "../AdminPage.styles";
import { RowAction } from "../RowAction";
import { TableContainer } from "../TableContainer";
import { TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { useMockPagination } from "../useMockPagination";
import { AlertHistoryModal } from "./AlertHistoryModal";
import { TriggeredAlertsModal } from "./TriggeredAlertsModal";

const SEARCH_FIELDS: (keyof IAdminAlert)[] = [
  "id",
  "name",
  "summaryText",
  "patientEmail",
  "createdByEmail",
  "wardName",
];

/** "SMS, Email, Call" — the channels staff are notified through, or "-". */
export function describeNotifications(alert: IAdminAlert): string {
  const channels = [
    alert.sendSms ? "SMS" : null,
    alert.sendEmail ? "Email" : null,
    alert.sendVoiceCall ? "Call" : null,
  ].filter(Boolean);
  return channels.length > 0 ? channels.join(", ") : "-";
}

export interface IAlertsPanelProps {
  /**
   * Restrict to one patient. Omit for every configured alert — this is
   * Retool's `patientId` module prop, which the Alerts page left blank and
   * the Patients page filled in.
   */
  patientId?: number;
  title?: string;
  /** Rows per page. Defaults to the shared /admin page size. */
  pageSize?: number;
}

/**
 * Configured measurement alerts — the portal port of Retool's `moduleAlerts`
 * (EN-5165).
 *
 * Shipped as a shared component because Retool embedded the same module in
 * the Patients and Archived Patients pages with `patientId` set; those
 * tickets (EN-5170, EN-5171) embed this rather than inlining another table.
 *
 *   <AlertsPanel />                        // every configured alert
 *   <AlertsPanel patientId={patient.id} /> // one patient's
 *
 * ⚠️ These are alert *configurations*, not firings. Triggered alerts are a
 * different endpoint (`/Retool/TriggeredAlerts`) and a different panel.
 */
export function AlertsPanel({
  patientId,
  title = "Alert configurations",
  pageSize,
}: IAlertsPanelProps) {
  const { openModal } = useModal();
  const { alerts, isLoading, isError } = useAdminAlerts(patientId);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: alerts,
    defaultPageSize: pageSize,
    searchFields: SEARCH_FIELDS,
  });

  const columns: ColumnDef<IAdminAlert, string>[] = useMemo(
    () => [
      {
        id: "id",
        header: "ID",
        accessorFn: (alert) => String(alert.id),
        enableSorting: true,
      },
      { accessorKey: "name", header: "Alert", enableSorting: true },
      {
        // `name` is fixed when the alert is created; an edit in the Insights
        // portal rewrites the trigger, so this is the column that actually
        // says what is configured today (QA on EN-5165).
        accessorKey: "summaryText",
        header: "Configuration",
        enableSorting: true,
      },
      {
        id: "notifies",
        header: "Notifies",
        accessorFn: describeNotifications,
        enableSorting: true,
      },
      { accessorKey: "patientEmail", header: "Patient", enableSorting: true },
      {
        accessorKey: "createdAt",
        header: "Created at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.createdAt, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "createdByEmail",
        header: "Created by",
        enableSorting: true,
      },
      {
        // Retool's column read "Hospital name". cms_hospital is a ward, and
        // the join runs through the CREATOR's staff row — so this is the
        // creating staff member's ward, not the patient's.
        accessorKey: "wardName",
        header: "Creator's ward",
        enableSorting: true,
      },
      {
        // Booleans go through an accessorFn returning a display string:
        // TableSorted renders "-" and skips the cell renderer for a falsy
        // value, which would hide every inactive alert's badge.
        id: "active",
        header: "Active",
        accessorFn: (alert) => (alert.active ? "Yes" : "No"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.active ? "active" : "inactive"}>
            {row.original.active ? "Yes" : "No"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "publish",
        header: "Published",
        accessorFn: (alert) => (alert.publish ? "Yes" : "No"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.publish ? "active" : "inactive"}>
            {row.original.publish ? "Yes" : "No"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="Triggered alerts"
              icon={<BellIcon />}
              onClick={() => {
                // What has actually FIRED for this row's patient — the
                // Patients page's panel, reachable from here.
                openModal(<TriggeredAlertsModal alert={row.original} />, {
                  width: "min(1280px, 96vw)",
                  maxHeight: "85vh",
                  showCloseButton: true,
                });
              }}
            />
            <RowAction
              label="History"
              icon={<CounterClockwiseClockIcon />}
              onClick={() => {
                openModal(<AlertHistoryModal alert={row.original} />, {
                  width: "min(1120px, 96vw)",
                  maxHeight: "85vh",
                  showCloseButton: true,
                });
              }}
            />
          </RowActionGroup>
        ),
      },
    ],
    []
  );

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
      </PanelHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by alert, configuration, patient, creator or ward..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the configured alerts. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-alerts"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "alerts",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PanelContainer>
  );
}
